import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { OutletScopeSelect } from "@/components/OutletScopeSelect";
import { OperationalBoundary } from "@/components/OperationalBoundary";
import { PosPrefsProvider, usePosPrefs, POS_LANGS } from "@/lib/posPrefs";
import { usePermissions } from "@/context/PermissionsContext";
import { ShoppingBag, Receipt, Store, Sunset, LogOut, Package, Type, BarChart3 } from "lucide-react";

const NAV = [
  ["/pos", "Billing", ShoppingBag, "billing"],
  ["/outlet-workspace", "Outlet", Store, "workspace"],
  ["/pos/orders", "Orders", Receipt, "orders"],
  ["/pos/day-end", "Day end", Sunset, "day-end"],
];
const REPORT_KEYS = ["report_sales", "report_item_wise", "report_category", "report_tax"];

function OutletShellInner({ children }) {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const { lang, setLang, largeText, setLargeText } = usePosPrefs();
  const { can } = usePermissions();
  const isArea = user?.role === "area_manager" || pathname.startsWith("/area-workspace");
  const canReports = REPORT_KEYS.some((k) => can(k));
  const baseNav = canReports ? [...NAV, ["/pos/reports", "Reports", BarChart3, "reports"]] : NAV;
  const links = isArea ? [["/area-workspace", "Overview", Store, "area-overview"], ["/area-workspace/records", "Records", Receipt, "area-records"], ["/area-workspace/stock", "Stock", Package, "area-stock"]] : baseNav;
  const navCols = isArea ? "grid-cols-3" : (canReports ? "grid-cols-5" : "grid-cols-4");
  return <div className={`min-h-screen flex flex-col bg-background text-foreground ${largeText ? "pos-large-text" : ""}`} data-testid="outlet-pos-shell">
    <header className="border-b bg-background" data-testid="outlet-pos-topbar">
      <div className="px-4 sm:px-6 py-3 flex flex-wrap items-center gap-3">
        <img src="/logo.png" alt="SSV" className="w-10 h-10 shrink-0 rounded-full" />
        <div className="flex-1 min-w-0"><p className="font-display font-bold text-base" data-testid="outlet-brand">SSV · {isArea ? "Area" : "Outlet"}</p><p className="text-xs text-muted-foreground break-words" data-testid="outlet-operator">{user?.name}</p></div>
        <select value={lang} onChange={(e) => setLang(e.target.value)} className="h-11 rounded-md border bg-background px-2 text-sm font-semibold" title="Language" aria-label="POS language" data-testid="outlet-pos-language-select">
          {POS_LANGS.map((l) => <option key={l.v} value={l.v}>{l.label}</option>)}
        </select>
        <Button variant={largeText ? "default" : "ghost"} size="icon" className="h-11 w-11" title={largeText ? "Large text: ON" : "Large text: OFF"} data-testid="outlet-pos-large-text-toggle" aria-pressed={largeText} onClick={() => setLargeText(!largeText)}><Type size={18} /></Button>
        <Button variant="ghost" size="icon" className="h-11 w-11" title="Sign out" data-testid="outlet-pos-logout" onClick={logout}><LogOut size={18} /></Button>
        {["admin", "manager"].includes(user?.role) && <Link to="/" className="text-sm underline" data-testid="outlet-back-management">Back office</Link>}
        <div className="basis-full sm:basis-auto sm:w-72 min-w-0"><OutletScopeSelect /></div>
      </div>
      <nav className={`grid ${navCols} border-t`} data-testid="outlet-pos-nav">{links.map(([to, label, Icon, id]) => <NavLink key={to} to={to} end data-testid={`outlet-nav-${id}`} className={({ isActive }) => `min-h-12 px-1 py-2 flex flex-col sm:flex-row justify-center items-center gap-1 sm:gap-2 text-xs sm:text-sm font-semibold transition-colors ${isActive ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}><Icon size={17} />{label}</NavLink>)}</nav>
    </header>
    <main className={`flex-1 min-w-0 ${pathname === "/pos" ? "" : "p-4 sm:p-7"}`}><OperationalBoundary>{children}</OperationalBoundary></main>
  </div>;
}

export default function OutletPOSLayout({ children }) {
  return <PosPrefsProvider><OutletShellInner>{children}</OutletShellInner></PosPrefsProvider>;
}
